import React from 'react';
import PropTypes from 'prop-types';
import { compose, lifecycle, withHandlers, withState } from 'recompose';
import { encode } from 'querystring';
import {
	UniversalSearchFilterContent,
	PriceRangeSlider,
	Toggle,
} from '@sparefoot/react-core';
import { TrackingClient } from '@sparefoot/tracking-client';
import EnclosedUnitOutlined from 'assets/images/search/EnclosedUnit-Outlined.svg';
import { newStorageSizeBuckets, newAmenities } from 'config/units';
import { getFilterParamForAmenity } from 'lib/search/parseAmenityFromPath';
import { Link, Image } from 'components/atoms';
import { Stars } from 'components/review/Stars';
import { nearMeAmenityKeyMap, nearMeVehicleKeyMap } from 'utils/filterCount';

import './SearchFilters.scss';

const storageAmenityParams = [
	'amenity',
	'twentyFourHourAccess',
	'climateControlled',
	'hasContactlessMoveins',
	'driveUp',
	'eGateAccess',
	'firstFloor',
	'surveillance',
	'hasOnlineMoveins',
	'kiosk',
	'power',
	'elevator',
	'freeTruckRental',
	'onlyWine',
	'onlyLocker',
];

const getAmenityKey = (amenity) => amenity.checkboxQueryParam || amenity.value;

function clearStorageAmenityParams(params) {
	storageAmenityParams.forEach((key) => {
		params[key] = undefined;
	});
}

function clearVehicleParams(params) {
	params.vehicle = undefined;
	params.vehicleTypes = undefined;
	params.vehicleAccommodation = undefined;
	params.minLength = undefined;
	params.maxLength = undefined;
	params.searchType = 'storage';
}

const vehicleTypeLengthRanges = {
	car: {
		minLength: '8',
		maxLength: '19.99',
		label: 'Car Storage',
	},
	boat: {
		minLength: '15',
		maxLength: '50',
		label: 'Boat Storage',
	},
	rv: {
		minLength: '20',
		maxLength: '50',
		label: 'RV Storage',
	},
	'outdoor-covered': {
		label: 'Covered Outdoor Storage',
		value: 'outdoor-covered',
		descriptionText: `You'll typically park your vehicle under a standalone roof alongside
			other vehicles like a carport.`,
		isAccommodation: true,
	},
	'outdoor-uncovered': {
		label: 'Uncovered Outdoor Storage',
		value: 'outdoor-uncovered',
		descriptionText:
			'An Outdoor Uncovered unit is a parking space similar to a traditional parking lot.',
		isAccommodation: true,
	},
};

export const enhance = compose(
	withState('isVehicleSelected', 'setVehicleSelected', (props) => props.queryParams.searchType === 'vehicle'),
	lifecycle({
		componentDidUpdate(prevProps) {
			if (prevProps.queryParams.searchType !== this.props.queryParams.searchType) {
				this.props.setVehicleSelected(this.props.queryParams.searchType === 'vehicle');
			}
		}
	}),
	withHandlers({
		handleVehicleAccommodationToggle: (props) => (amenity, checked) => {
			const newParams = { ...props.queryParams };
			const amenityValue = amenity.value; // 'outdoor-uncovered' or 'outdoor-covered'

			const isCurrentlySelected =
				props.queryParams.vehicleAccommodation === amenityValue;

			if (checked && !isCurrentlySelected) {
				newParams.vehicleAccommodation = amenityValue;
				newParams.searchType = 'vehicle';
				clearStorageAmenityParams(newParams);
				props.setVehicleSelected(true);
			} else if (!checked && isCurrentlySelected) {
				newParams.vehicleAccommodation = undefined;
				if (!newParams.vehicleTypes) {
					newParams.searchType = 'storage';
					newParams.vehicle = undefined;
					props.setVehicleSelected(false);
				}
			}

			TrackingClient.trackSearchFiltered({
				search_filters: encode(props.queryParams),
				search_label: `vehicleAccommodation-${amenityValue}-${checked}`,
				search_term: 'vehicleAccommodation',
			});

			props.onFilter(newParams);
		},
	}),
	withHandlers({
		handlePriceRangeChange:
			(props) =>
			({ minPrice, maxPrice }) => {
				const newParams = {
					...props.queryParams,
					minPrice: minPrice > 0 ? minPrice : undefined,
					maxPrice:
						maxPrice && maxPrice <= 500 ? maxPrice : undefined,
				};

				TrackingClient.trackSearchFiltered({
					search_filters: encode(props.queryParams),
					search_label: `price-${minPrice}-${maxPrice}`,
					search_term: 'price',
				});

				props.onFilter(newParams);
			},

		handleSpecialToggle: (props) => (value) => {
			const newParams = {
				...props.queryParams,
				hasSpecial: value ? value : undefined,
			};

			TrackingClient.trackSearchFiltered({
				search_filters: encode(props.queryParams),
				search_label: `hasSpecial-${value}`,
				search_term: 'hasSpecial',
			});

			props.onFilter(newParams);
		},

		handleSizeSelect: (props) => (selOption) => {
			const newParams = { ...props.queryParams };

			if (props.isVehicleSelected) {
				const sizeOption = newStorageSizeBuckets.parking.find(
					(option) => option.value === selOption.value,
				);
				if (sizeOption) {
					newParams.minLength = sizeOption.value;
					newParams.maxLength = sizeOption.maxLength;
					newParams.sqft = undefined;
					newParams.onlyLocker = undefined;
					TrackingClient.trackSearchFiltered({
						search_filters: encode(props.queryParams),
						search_label: `minLength-${sizeOption.value}`,
						search_term: 'minLength',
					});
				}
			} else {
				const sizeOption = newStorageSizeBuckets['storage-only'].find(
					(option) => option.value === selOption.value,
				);
				if (sizeOption) {
					if (sizeOption.isLocker) {
						newParams.onlyLocker = true;
						newParams.sqft = undefined;
					} else {
						newParams.sqft = sizeOption.value;
						newParams.onlyLocker = undefined;
					}
					newParams.minLength = undefined;
					newParams.maxLength = undefined;
					TrackingClient.trackSearchFiltered({
						search_filters: encode(props.queryParams),
						search_label: `sqft-${sizeOption.value}`,
						search_term: 'sqft',
					});
				}
			}

			props.onFilter(newParams);
		},

		handleReviewSelect: (props) => (reviewOption) => {
			const newParams = {
				...props.queryParams,
				averageReview:
					reviewOption.value === '4' ? reviewOption.value : undefined,
			};

			TrackingClient.trackSearchFiltered({
				search_filters: encode(props.queryParams),
				search_label: `averageReview-${reviewOption.value}`,
				search_term: 'averageReview',
			});

			props.onFilter(newParams);
		},

		handleAmenityToggle: (props) => (amenity, checked) => {
			const newParams = { ...props.queryParams };
			const amenityKey = getAmenityKey(amenity);

			if (checked) {
				newParams[amenityKey] = true;
				clearVehicleParams(newParams);
			} else {
				newParams[amenityKey] = undefined;
				// Clear the amenity param when unchecking the filter that corresponds
				// to the current amenity landing page context. This prevents
				// SearchPageWrapper from re-injecting the filter on the next render.
				if (
					newParams.amenity &&
					getFilterParamForAmenity(newParams.amenity) === amenityKey
				) {
					newParams.amenity = undefined;
				}
			}

			TrackingClient.trackSearchFiltered({
				search_filters: encode(props.queryParams),
				search_label: `${amenityKey}-${checked}`,
				search_term: amenityKey,
			});

			props.onFilter(newParams);
		},

		handleVehicleTypeToggle: (props) => (option, checked) => {
			const { queryParams } = props;
			const vehicleType = option.value;
			const isAccommodation =  vehicleTypeLengthRanges[vehicleType]?.isAccommodation;
			const currentTypes = props.queryParams.vehicleTypes
					? props.queryParams.vehicleTypes
							.split(',')
							.map((t) => t.trim())
					: [];
			let newTypes = [];
			if (currentTypes.includes(vehicleType)) {
				newTypes = currentTypes.filter((t) => t !== vehicleType);
			} else {
				newTypes = isAccommodation ? currentTypes : [...currentTypes, vehicleType];
			}
		
			if (isAccommodation) {
				props.handleVehicleAccommodationToggle(option, checked);
			} else {
				// true if an accommodation remains selected after this toggle
				const isAccommodationSelected =
					!!props.queryParams.vehicleAccommodation;

				props.setVehicleSelected(
					newTypes.length > 0 || isAccommodationSelected,
				);

				const newParams = { ...props.queryParams };

				if (newTypes.length > 0) {
					const minLengths = newTypes.map((type) =>
						parseFloat(
							vehicleTypeLengthRanges[type]?.minLength || 0,
						),
					);
					const maxLengths = newTypes.map((type) =>
						parseFloat(
							vehicleTypeLengthRanges[type]?.maxLength || 999,
						),
					);

					newParams.vehicleTypes = newTypes.join(',');
					newParams.minLength = String(Math.min(...minLengths));
					newParams.maxLength = String(Math.max(...maxLengths));
					newParams.searchType = 'vehicle';
					newParams.vehicle = newTypes[newTypes.length - 1];
					newParams.sqft = undefined;
					clearStorageAmenityParams(newParams);

					TrackingClient.trackSearchFiltered({
						search_filters: encode(props.queryParams),
						search_label: `vehicleTypes-${vehicleType}`,
						search_term: 'vehicleTypes',
						vehicle_types_selected: newTypes.join(','),
					});
				} else {
					newParams.minLength = undefined;
					newParams.maxLength = undefined;
					newParams.vehicle = undefined;
					newParams.vehicleTypes = undefined;
					if (!newParams.vehicleAccommodation) {
						newParams.searchType = 'storage';
					}
				}
				props.onFilter(newParams);
			}
		},
	}),
);

const SearchFilters = ({
	facetCounts,
	queryParams,
	onFilter,
	handlePriceRangeChange,
	handleSpecialToggle,
	handleReviewSelect,
	handleAmenityToggle,
	handleSizeSelect,
	handleVehicleTypeToggle,
	onShowUnitGuide,
	nearMeParams,
	isVehicleSelected
}) => {
	const vehicleTypesForFilter = queryParams.vehicleTypes
		? queryParams.vehicleTypes.split(',').map((t) => t.trim())
		: queryParams.vehicle
			? [queryParams.vehicle]
			: nearMeParams.vehicle
				? [nearMeVehicleKeyMap[nearMeParams.vehicle]]
				: [];

	const selectedVehicleTypes = queryParams.vehicleAccommodation
		? [...vehicleTypesForFilter, queryParams.vehicleAccommodation]
		: vehicleTypesForFilter;

	const hasSizeFilter = isVehicleSelected
		? newStorageSizeBuckets['parking'].some(
				(opt) => opt.value === queryParams.minLength,
			)
		: !!(queryParams.sqft || queryParams.onlyLocker);
	const hasReviewFilter = !!queryParams.averageReview;
	const hasAmenityFilter = newAmenities.some((amenity) => {
		const key = getAmenityKey(amenity);
		return !!queryParams[key];
	});
	const initialVisibleCount = 5;
	const isShowAllAmenities = newAmenities.slice(initialVisibleCount).some((amenity) => {
		const key = getAmenityKey(amenity);
		return !!queryParams[key];
	});
	const hasVehicleFilter = !!(queryParams.vehicleTypes || queryParams.vehicleAccommodation);

	const filterConfig = [
		{
			id: 'price',
			type: 'custom',
			title: 'Price',
			column: '0',
			defaultOpen: true,
			component: (
				<>
					<PriceRangeSlider
						minPrice={queryParams.minPrice}
						maxPrice={queryParams.maxPrice}
						maxThumbFloor={10}
						onPriceChange={handlePriceRangeChange}
						onTrack={(data) => {
							TrackingClient.trackSearchFiltered({
								search_label: `price-slider-${data.minPrice}-${data.maxPrice}`,
								search_term: 'price-slider',
							});
						}}
					/>
					<Toggle
						id="special-offers"
						label="Only Show Facilities With Deals"
						value={queryParams.hasSpecial === 'true'}
						onChange={handleSpecialToggle}
						trackingLabel="special-offers-toggle"
						input={{
							value: !!queryParams.hasSpecial,
						}}
					/>
				</>
			),
		},
		{
			id: 'size',
			type: 'radio',
			title: isVehicleSelected ? 'Vehicle Unit Sizes' : 'Unit Sizes',
			column: '0',
			selectedValue: isVehicleSelected
				? queryParams.minLength
				: queryParams.onlyLocker
					? '0-5'
					: nearMeAmenityKeyMap[nearMeParams?.storageAmenity || '']
						? nearMeAmenityKeyMap[nearMeParams.storageAmenity]
						: queryParams.sqft,
			defaultOpen: hasSizeFilter,
			options: newStorageSizeBuckets[isVehicleSelected  ? 'parking' : 'storage-only'].map(
				(item) => ({
					label:  <><span>{item.label}</span>{!isVehicleSelected  && <span className='sqft'>{item.sqftText} sq ft</span>}</> ,
					value: item.value,
				}),
			),
			onSelect: handleSizeSelect,
			className: 'unit-sizes-filter',
			renderExtra: () => (
				<div className="unit-size-guide-link">
					<Image src={EnclosedUnitOutlined} />
					<Link onClick={onShowUnitGuide}>
						{' '}
						View our storage unit size guide
					</Link>
				</div>
			),
		},
		{
			id: 'reviews',
			type: 'radio',
			title: 'Ratings',
			column: '0',
			defaultOpen: hasReviewFilter,
			options: [
				{ label: 'All Ratings', value: 'all' },
				{
					label: (
						<>
							<Stars
								rating="4"
								size="standard"
							/>
							<span>& up</span>
						</>
					),
					value: '4',
				},
			],
			selectedValue: queryParams.averageReview || 'all',
			onSelect: (option) => handleReviewSelect(option),
		},
		{
			id: 'storageAmenities',
			type: 'checkbox',
			title: 'STORAGE AMENITIES',
			column: '1',
			defaultOpen: hasAmenityFilter,
			options: newAmenities,
			isSelected: (amenity) => {
				const amenityKey = getAmenityKey(amenity);
				if (!!queryParams[amenityKey]) return true;
				if (
					nearMeAmenityKeyMap[nearMeParams.storageAmenity] ===
					amenity.value
				)
					return true;
				else return false;
				
			},
			onToggle: handleAmenityToggle,
			isDisabled: (amenity) => {
				const amenityKey = getAmenityKey(amenity);
				const isInObject = JSON.stringify(facetCounts || {}).includes(
					amenityKey,
				);
				const isZero = facetCounts?.[amenityKey] === 0;
				const isSelected = !!queryParams[amenityKey];
				return isInObject && isZero && !isSelected;
			},
			showMoreEnabled: true,
			isShowAll: isShowAllAmenities,
			initialVisibleCount: initialVisibleCount,
		},
		{
			id: 'vehicleStorage',
			type: 'checkbox',
			title: 'VEHICLE STORAGE',
			column: '1',
			defaultOpen: hasVehicleFilter,
			options: Object.entries(vehicleTypeLengthRanges).map(
				([type, config]) => ({
					label: config.label,
					value: type,
				}),
			),
			isSelected: (option) => {
				return selectedVehicleTypes.includes(option.value);
			},
			isDisabled: (amenity) => {
				const amenityKey = amenity.value;
				const isInObject = JSON.stringify(facetCounts || {}).includes(
					amenityKey.replace('outdoor-', ''), // facet is just 'covered' or 'uncovered'
				);
				const isZero =
					facetCounts?.[amenityKey.replace('outdoor-', '')] === 0;
				const isSelected =
					queryParams.vehicleAccommodation === amenityKey;
				return isInObject && isZero && !isSelected;
			},
			onToggle: handleVehicleTypeToggle,
		},
	];

	return (
		<UniversalSearchFilterContent
			columns={2}
			queryParams={queryParams}
			filterConfig={filterConfig}
			onFilter={onFilter}
			className="reactfoot-filters"
		/>
	);
};

SearchFilters.propTypes = {
	queryParams: PropTypes.object.isRequired,
	onFilter: PropTypes.func.isRequired,
	handlePriceRangeChange: PropTypes.func.isRequired,
	handleSpecialToggle: PropTypes.func.isRequired,
	handleReviewSelect: PropTypes.func.isRequired,
	handleAmenityToggle: PropTypes.func.isRequired,
	handleSizeSelect: PropTypes.func.isRequired,
	handleVehicleAccommodationToggle: PropTypes.func.isRequired,
	handleVehicleTypeToggle: PropTypes.func.isRequired,
	onShowUnitGuide: PropTypes.func.isRequired,
	facetCounts: PropTypes.object,
	nearMeParams: PropTypes.object,
	isVehicleSelected: PropTypes.bool,
};

SearchFilters.defaultProps = {
	facetCounts: {},
	nearMeParams: {},
};

export default enhance(SearchFilters);
